/**
 * Pasquill atmospheric stability classes. The time series returns the class as
 * a number (1 = A … 6 = F); the letter and its description are display-only.
 */
export const PASQUILL_CLASS_LETTERS = ["A", "B", "C", "D", "E", "F"] as const;

export type PasquillClassLetter = (typeof PASQUILL_CLASS_LETTERS)[number];

/**
 * Class letter for a raw series value, or `null` when the value is missing or
 * outside 1–6. Non-integers are rejected rather than rounded: a value between
 * two classes is bad data, and guessing would hide it.
 */
export function pasquillClassLetter(
  value: number | null | undefined,
): PasquillClassLetter | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isInteger(numeric)) return null;
  return PASQUILL_CLASS_LETTERS[numeric - 1] ?? null;
}

/**
 * i18n keys for the class descriptions, e.g. "Neutraal" for D. Spelled out
 * rather than built from the letter so the keys stay literal types that
 * `t()` can resolve.
 */
export const PASQUILL_CLASS_I18N_KEYS = {
  A: "widgets.pasquill.class_a",
  B: "widgets.pasquill.class_b",
  C: "widgets.pasquill.class_c",
  D: "widgets.pasquill.class_d",
  E: "widgets.pasquill.class_e",
  F: "widgets.pasquill.class_f",
} as const satisfies Record<PasquillClassLetter, string>;
